import { useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { plainText } from './propertyUtils';
import brandFillImage from '../../assets/home-brand-fill.webp';

/** Schema default; resolved to the bundled asset so it ships with the JS build. */
const FILL_DEFAULT_PATH = '/home-brand-fill.webp';

const imageDef: SlotGroupDefinition = {
  label: 'Brand Fill Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '/home-brand-fill.webp',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Aerial view of an American Landmark community and pool',
    },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/our-communities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Explore Properties',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function cssUrl(url: string) {
  return `url("${url.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`;
}

/** 0 when the heading enters the bottom of the viewport, 1 when it leaves the top. */
function wordmarkScrollProgress(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const viewH = window.innerHeight || 1;
  const total = rect.height + viewH;
  return Math.min(1, Math.max(0, (viewH - rect.top) / total));
}

export function HomeBrand() {
  const image = useSlotGroup('homebrand.image', imageDef);
  const cta = useSlotGroup('homebrand.cta', ctaDef);
  const strokeText =
    plainText(useContentToken('homebrand.marquee')) || 'WHAT MAKES US DIFFERENT';
  const rawFill = image.image || '';
  const fillUrl = !rawFill || rawFill === FILL_DEFAULT_PATH ? brandFillImage : rawFill;
  const wordmarkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = wordmarkRef.current;
    const heading = host?.querySelector<HTMLElement>('.ala-parallax-wordmark');
    if (!heading) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      heading.style.setProperty('--ala-wordmark-p', wordmarkScrollProgress(heading).toFixed(4));
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    // Capture so a nested preview scroller updates the fill, not only window scroll.
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="flex w-full flex-col items-center overflow-clip bg-[rgb(var(--ala-surface))] pt-24 pb-6 md:pb-20">
      <div className="flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 md:px-8">
        <EditableSlotGroup groupPath="homebrand.image" definition={imageDef}>
          <div className="sr-only">{image.alt || 'Aerial view of an American Landmark community and pool'}</div>
        </EditableSlotGroup>

        <div className="flex flex-col items-center gap-8 text-center">
          <div ref={wordmarkRef} className="w-full">
            <RichTextContent
              path="homebrand.wordmark"
              label="Wordmark"
              as="h2"
              className="ala-parallax-wordmark w-full break-words text-center font-heading text-[21vw] font-bold md:text-[clamp(60px,14.78vw,240px)] leading-[0.85] tracking-[-0.086em]"
              style={{ ['--ala-wordmark-image' as string]: cssUrl(fillUrl) }}
            />
          </div>

          <RichTextContent
            path="homebrand.body"
            label="Body"
            as="p"
            className="max-w-[620px] font-heading text-[clamp(16px,1.72vw,28px)] font-medium leading-[1.3] tracking-[-0.039em] text-[rgb(var(--ala-text-muted))]"
          />

          <EditableSlotGroup groupPath="homebrand.cta" definition={ctaDef}>
            <a
              href={cta.url || '/our-communities'}
              target={cta._target || '_self'}
              className="inline-flex items-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-3.5 font-heading text-sm uppercase tracking-wide text-[rgb(var(--ala-on-secondary))] transition hover:bg-[#3d7fd9]"
            >
              {cta.label || 'Explore Properties'}
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      {/* Stroke-draw band — matches reference: outline → fill, 4s alternate */}
      <div className="mt-8 flex w-full justify-center py-2">
        <EditableToken path="homebrand.marquee" as="div" label="Stroke Text" className="w-full">
          <div className="mx-auto w-full max-w-[1920px] px-4">
            <svg
              className="stroke-text-svg block h-auto w-full"
              viewBox="0 0 2800 480"
              preserveAspectRatio="xMidYMid meet"
              style={{ fontWeight: 800 }}
              role="img"
              aria-label={strokeText}
            >
              <text
                x="50%"
                y="50%"
                dy=".32em"
                textAnchor="middle"
                className="text-logo"
                fill="#8C8C8C"
                style={{ fontSize: 220, fontWeight: 800 }}
              >
                {strokeText}
              </text>
            </svg>
          </div>
        </EditableToken>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeBrand',
  displayName: 'Home Brand Band',
  tokens: {
    'homebrand.wordmark': {
      label: 'Wordmark',
      type: 'richtext',
      defaultValue: '<p>American<br/>Landmark</p>',
    },
    'homebrand.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>We shape spaces by blending visionary design with purposeful functionality creating environments.</p>',
    },
    'homebrand.marquee': {
      label: 'Stroke Text',
      type: 'text',
      defaultValue: 'WHAT MAKES US DIFFERENT',
    },
  },
  slotGroups: {
    'homebrand.image': imageDef,
    'homebrand.cta': ctaDef,
  },
};

(HomeBrand as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeBrand;
